import { Button, Container, Group, Stack } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { Loader } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { Table } from "~/components/table/Table"
import {
  findCertificateRequestsForSupplierQueryKey,
  useFindCertificateRequestsForSupplier,
  useUpdateManyCertificateRequests,
} from "~/gen/api/hooks"
import { useCertificateRequestsBatchModalContext } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/store/CertificateRequestsBatchModalContext"
import { useBatchUpdateCertificateRequestsColumns } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/certificateRequestsBatchModal/useBatchUpdateCertificateRequestsColumns"

type Props = {
  close: () => void
}

export function BatchUpdateTransactionCertificateRequestsTable({
  close,
}: Props) {
  const { data: certificateRequests, isLoading: isLoadingCertificateRequests } =
    useFindCertificateRequestsForSupplier({
      category: "TRANSACTION",
      status: "OPENED",
    })

  const { certificateRequestId, certificateId } =
    useCertificateRequestsBatchModalContext()

  const { mutate: updateManyCertificateRequests, isLoading: isUpdating } =
    useUpdateManyCertificateRequests({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: findCertificateRequestsForSupplierQueryKey(),
          })
          close()

          notifications.show({
            message: t("certificateBatchModal.success", {
              count: checklist.length,
            }),
            color: "green",
          })
        },
      },
    })

  const columns = useBatchUpdateCertificateRequestsColumns()
  const [rowSelection, setRowSelection] = useState({})

  const tableData = useMemo(() => {
    return (
      certificateRequests?.data.filter(
        (certificateRequest) => certificateRequest.id !== certificateRequestId
      ) ?? []
    )
  }, [certificateRequests, certificateRequestId])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      columnPinning: { left: ["select"] },
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
  })

  const checklist = useMemo(() => Object.keys(rowSelection), [rowSelection])

  const { t } = useTranslation("traceability")

  if (isLoadingCertificateRequests) {
    return <Loader fullPage />
  }

  return (
    <Stack>
      <Container mah="80%" m={0} maw="100%">
        <Table table={table} pagination={false} />
      </Container>
      <Group
        justify="center"
        h="10vh"
        pos="sticky"
        bottom={0}
        bg="white"
        w="100%"
        style={{
          borderTop: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <Button
          disabled={isUpdating || checklist.length === 0}
          loading={isUpdating}
          onClick={() => {
            updateManyCertificateRequests({
              ids: checklist,
              update: {
                certificateId: certificateId ?? undefined,
              },
            })
          }}
        >
          {t("batchModal.applyButton", {
            type: t("batchModal.labels.certificate"),
            itemCount: checklist.length,
          })}
        </Button>
      </Group>
    </Stack>
  )
}
